"use client";

import { FormEvent, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Image from "../ui/OptimizedImage";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth, useClerk, useUser } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import { apiFetch, apiFetchAuthed } from "../../lib/api";
import { displayEmailOrNull } from "../../lib/synthetic-email";
import { queryKeys } from "../../lib/query-keys";
import { buildEventPath } from "../../lib/routes";
import { useNotifications } from "../../lib/useNotifications";

import {
    OnboardingStatus,
    Category,
    City,
    SearchEvent,
    NavItem,
    defaultCities,
    CITY_STORAGE_KEY,
    fallbackNavItems,
    parseDateKey,
    toDateKey,
} from "./landing-nav-types";
import { SearchPanel, MobileSearchPanel } from "./SearchPanel";
import { NotificationsPanel } from "./NotificationsPanel";
import { CityPicker } from "./CityPicker";
import { DateCalendar } from "./DateCalendar";
import { PriceFilter } from "./PriceFilter";
import { ProfileMenu } from "./ProfileMenu";
import { MobileNav } from "./MobileNav";
import { GuestBottomNav } from "./GuestBottomNav";

function LandingNavInner() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { user, isSignedIn } = useUser();
    const { getToken } = useAuth();
    const { signOut } = useClerk();
    const [isHost, setIsHost] = useState<boolean | null>(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const router = useRouter();

    const {
        notifications,
        unreadCount,
        isLoading: isLoadingNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification: removeNotification,
    } = useNotifications();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const searchParam = searchParams?.get("search") ?? "";
    const cityParam = searchParams?.get("city");
    const cityIdParam = searchParams?.get("cityId") ?? "";
    const dateParam = searchParams?.get("date") ?? "";
    const [searchValue, setSearchValue] = useState(searchParam);
    const [cityValue, setCityValue] = useState(cityParam ?? "");
    const [cityIdValue, setCityIdValue] = useState(cityIdParam);
    const [dateValue, setDateValue] = useState(dateParam);
    const [searchResults, setSearchResults] = useState<SearchEvent[]>([]);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [isCityMenuOpen, setIsCityMenuOpen] = useState(false);
    const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
    const [dateMenuAnchor, setDateMenuAnchor] = useState<"desktop" | "mobile" | null>(
        null,
    );
    const [isPriceMenuOpen, setIsPriceMenuOpen] = useState(false);
    const minPriceParam = searchParams?.get("minPrice") ?? "";
    const maxPriceParam = searchParams?.get("maxPrice") ?? "";
    const [minPrice, setMinPrice] = useState(
        minPriceParam ? String(Math.round(Number(minPriceParam) / 100)) : "",
    );
    const [maxPrice, setMaxPrice] = useState(
        maxPriceParam ? String(Math.round(Number(maxPriceParam) / 100)) : "",
    );
    const desktopCityRef = useRef<HTMLDivElement | null>(null);
    const mobileCityRef = useRef<HTMLDivElement | null>(null);
    const desktopSearchRef = useRef<HTMLFormElement | null>(null);
    const mobileSearchRef = useRef<HTMLFormElement | null>(null);
    const desktopDateRef = useRef<HTMLDivElement | null>(null);
    const mobileDateRef = useRef<HTMLDivElement | null>(null);
    const desktopPriceRef = useRef<HTMLDivElement | null>(null);
    const mobilePriceRef = useRef<HTMLDivElement | null>(null);
    const profileRef = useRef<HTMLDivElement | null>(null);
    const mobileNotificationsRef = useRef<HTMLDivElement | null>(null);
    const desktopNotificationsRef = useRef<HTMLDivElement | null>(null);
    const searchRequestRef = useRef(0);
    const searchCacheRef = useRef(
        new Map<string, { value: SearchEvent[]; expiresAt: number }>(),
    );
    const searchAbortRef = useRef<AbortController | null>(null);
    const notificationToggleLockRef = useRef(0);
    const searchFocusRef = useRef(false);
    const hasUserSelectedCityRef = useRef(false);
    const hasRestoredCityRef = useRef(false);
    const trimmedSearch = searchValue.trim();
    const hasQuery = trimmedSearch.length >= 2;

    useEffect(() => {
        const handleScroll = () => {
            setIsCollapsed(window.scrollY > 80);
        };
        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        if (!isCollapsed) {
            return;
        }
        setIsSearchOpen(false);
        setIsDateMenuOpen(false);
        setIsPriceMenuOpen(false);
        searchFocusRef.current = false;
        setIsSearchFocused(false);
    }, [isCollapsed]);

    const { data: onboardingStatus, isError: onboardingError } = useQuery({
        queryKey: queryKeys.onboarding.status(),
        queryFn: () =>
            apiFetchAuthed<OnboardingStatus>("onboarding/status", {}, () => getToken()),
        enabled: isSignedIn,
        staleTime: 60_000,
    });

    const { data: categoryData } = useQuery({
        queryKey: queryKeys.events.categories(),
        queryFn: () => apiFetch<Category[]>("categories"),
        staleTime: 30 * 60_000,
    });

    const navItems = useMemo<NavItem[]>(() => {
        if (categoryData && categoryData.length > 0) {
            const categoryItems = categoryData.slice(0, 10).map((category) => ({
                id: category.id,
                label: category.name,
                href: `/events?category=${encodeURIComponent(category.name)}`,
            }));
            return [{ id: "events", label: "Events", href: "/events" }, ...categoryItems];
        }
        return fallbackNavItems;
    }, [categoryData]);

    useEffect(() => {
        if (!isSignedIn) {
            setIsHost(null);
            return;
        }
        if (onboardingStatus) {
            setIsHost(Boolean(onboardingStatus.hasHostProfile));
            return;
        }
        if (onboardingError) {
            console.warn("[landing] failed to load host status");
            setIsHost(true);
        }
    }, [isSignedIn, onboardingError, onboardingStatus]);

    useEffect(() => {
        setSearchValue(searchParam);
    }, [searchParam]);

    useEffect(() => {
        if (cityParam !== null) {
            setCityValue(cityParam);
        }
        if (cityIdParam) {
            setCityIdValue(cityIdParam);
        }
    }, [cityIdParam, cityParam]);

    useEffect(() => {
        setMinPrice(minPriceParam ? String(Math.round(Number(minPriceParam) / 100)) : "");
        setMaxPrice(maxPriceParam ? String(Math.round(Number(maxPriceParam) / 100)) : "");
    }, [minPriceParam, maxPriceParam]);

    useEffect(() => {
        if (!hasQuery) {
            setSearchResults([]);
            setIsSearchOpen(false);
            setIsSearching(false);
            return;
        }

        const requestId = ++searchRequestRef.current;
        const normalizedCity = cityValue.trim().toLowerCase();
        const cacheKey = `${trimmedSearch.toLowerCase()}|${normalizedCity}|${cityIdValue}`;
        const cached = searchCacheRef.current.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
            setSearchResults(cached.value);
            if (searchFocusRef.current) {
                setIsSearchOpen(true);
            }
        }
        setIsSearching(!cached);
        const timer = setTimeout(async () => {
            try {
                searchAbortRef.current?.abort();
                const controller = new AbortController();
                searchAbortRef.current = controller;
                const params = new URLSearchParams();
                params.set("search", trimmedSearch);
                params.set("limit", "6");
                params.set("upcoming", "true");
                params.set("suggest", "true");
                if (cityIdValue) {
                    params.set("cityId", cityIdValue);
                } else if (cityValue.trim()) {
                    params.set("city", cityValue.trim());
                }
                const data = await apiFetch<SearchEvent[]>(
                    `events?${params.toString()}`,
                    { signal: controller.signal },
                );
                if (searchRequestRef.current !== requestId) return;
                searchCacheRef.current.set(cacheKey, {
                    value: data,
                    expiresAt: Date.now() + 5 * 60_000,
                });
                setSearchResults(data);
                if (searchFocusRef.current) {
                    setIsSearchOpen(true);
                }
            } catch (err) {
                if (err instanceof Error && err.name === "AbortError") {
                    return;
                }
                if (searchRequestRef.current !== requestId) return;
                console.warn("[landing] failed to load search suggestions", err);
                setSearchResults([]);
                if (searchFocusRef.current) {
                    setIsSearchOpen(true);
                }
            } finally {
                if (searchRequestRef.current === requestId) {
                    setIsSearching(false);
                }
            }
        }, cached ? 120 : 180);

        return () => clearTimeout(timer);
    }, [cityValue, cityIdValue, hasQuery, trimmedSearch]);

    const { data: citiesData } = useQuery({
        queryKey: queryKeys.events.cities(),
        queryFn: () => apiFetch<City[]>("cities"),
        staleTime: 30 * 60_000,
    });

    useEffect(() => {
        if (!isCityMenuOpen && !isSearchOpen && !isDateMenuOpen && !isProfileOpen && !isNotificationsOpen && !isPriceMenuOpen) {
            return;
        }
        const handleClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (profileRef.current?.contains(target)) return;
            if (mobileNotificationsRef.current?.contains(target)) return;
            if (desktopNotificationsRef.current?.contains(target)) return;
            if (desktopCityRef.current?.contains(target)) return;
            if (mobileCityRef.current?.contains(target)) return;
            if (desktopSearchRef.current?.contains(target)) return;
            if (mobileSearchRef.current?.contains(target)) return;
            if (desktopDateRef.current?.contains(target)) return;
            if (mobileDateRef.current?.contains(target)) return;
            if (desktopPriceRef.current?.contains(target)) return;
            if (mobilePriceRef.current?.contains(target)) return;
            setIsCityMenuOpen(false);
            setIsSearchOpen(false);
            setIsDateMenuOpen(false);
            setIsProfileOpen(false);
            setIsNotificationsOpen(false);
            setIsPriceMenuOpen(false);
            searchFocusRef.current = false;
            setIsSearchFocused(false);
        };
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsCityMenuOpen(false);
                setIsSearchOpen(false);
                setIsDateMenuOpen(false);
                setIsProfileOpen(false);
                setIsNotificationsOpen(false);
                setIsPriceMenuOpen(false);
                searchFocusRef.current = false;
                setIsSearchFocused(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [isCityMenuOpen, isDateMenuOpen, isNotificationsOpen, isPriceMenuOpen, isProfileOpen, isSearchOpen]);

    const displayName =
        user?.fullName ||
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        "Account";
    const search = searchParams?.toString() ?? "";
    const redirectTarget = pathname
        ? search
            ? `${pathname}?${search}`
            : pathname
        : "/";
    const authHref = `/auth?redirect=${encodeURIComponent(redirectTarget)}`;
    const accountHref = isSignedIn
        ? isHost === true
            ? "/dashboard"
            : "/"
        : authHref;
    const handleProfileNavigation = (href: string) => {
        setIsProfileOpen(false);
        router.push(href);
    };
    const toggleNotificationsMenu = () => {
        const now = Date.now();
        if (now - notificationToggleLockRef.current < 150) {
            return;
        }
        notificationToggleLockRef.current = now;
        setIsNotificationsOpen((prev) => !prev);
        setIsProfileOpen(false);
        setIsCityMenuOpen(false);
    };
    const guestMenuItems = [
        { label: "My tickets", href: "/my-tickets" },
        { label: "My profile", href: "/profile" },
        { label: "Host an Event", href: "/onboarding?start=hostType" },
    ];
    const hostMenuItems = [
        { label: "Host an Event", href: "/host/events/new" },
        { label: "My events", href: "/dashboard?tab=events" },
        { label: "My tickets", href: "/my-tickets" },
        { label: "Analytics", href: "/dashboard?tab=analytics" },
        { label: "Settings", href: "/dashboard?tab=settings" },
    ];
    const profileMenuItems =
        isHost === true
            ? hostMenuItems
            : guestMenuItems;
    const cityLabel = cityValue === "" ? "All cities" : cityValue;
    const cityOptions = useMemo(() => {
        const source = [
            // Pin Dubai first as a non-India example city (real scraped events).
            { id: "city-dubai", name: "Dubai" },
            ...(citiesData && citiesData.length > 0 ? citiesData : defaultCities),
        ];
        const seen = new Set<string>();
        return source.filter((city) => {
            const key = city.name.toLowerCase();
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }, [citiesData]);
    const buildEventsUrl = (
        nextSearch: string,
        nextCity: string,
        nextDate: string,
        nextCityId?: string,
        nextMinPrice?: string,
        nextMaxPrice?: string,
    ) => {
        const params = new URLSearchParams();
        const normalizedSearch = nextSearch.trim();
        const normalizedCity = nextCity.trim();
        const normalizedDate = nextDate.trim();
        const isEventsPage = pathname?.startsWith("/events");
        const isMapPage = pathname?.startsWith("/map");
        const basePath = isMapPage ? "/map" : isEventsPage ? "/events" : "/";
        if (normalizedSearch) {
            params.set("search", normalizedSearch);
        }
        if (nextCityId) {
            params.set("cityId", nextCityId);
        }
        if (normalizedCity) {
            params.set("city", normalizedCity);
        }
        if (normalizedDate) {
            params.set("date", normalizedDate);
        }
        // minPrice and maxPrice are component state variables defined above
        const resolvedMin = nextMinPrice !== undefined ? nextMinPrice : minPrice;
        const resolvedMax = nextMaxPrice !== undefined ? nextMaxPrice : maxPrice;
        if (resolvedMin) {
            params.set("minPrice", String(Number(resolvedMin) * 100));
        }
        if (resolvedMax) {
            params.set("maxPrice", String(Number(resolvedMax) * 100));
        }
        const query = params.toString();
        return query ? `${basePath}?${query}` : basePath;
    };
    useEffect(() => {
        if (hasRestoredCityRef.current) {
            return;
        }
        hasRestoredCityRef.current = true;
        if (cityParam || cityIdParam) {
            return;
        }
        if (typeof window === "undefined") {
            return;
        }
        try {
            const stored = window.localStorage.getItem(CITY_STORAGE_KEY);
            if (!stored) {
                return;
            }
            const resolvedPath =
                typeof window !== "undefined" ? window.location.pathname : pathname;
            // Only auto-apply the stored city on the home and events-list pages —
            // NOT on deeper /events/<city>/<slug> detail pages (would bounce away).
            const allowAutoRoute =
                resolvedPath === "/" || resolvedPath === "/events";
            const parsed = JSON.parse(stored) as { value?: string; id?: string } | null;
            const storedValue = parsed?.value ?? "";
            const storedId = parsed?.id ?? "";
            hasUserSelectedCityRef.current = true;
            setCityValue(storedValue);
            setCityIdValue(storedId);
            if ((storedValue || storedId) && allowAutoRoute) {
                router.replace(
                    buildEventsUrl(searchValue, storedValue, dateValue, storedId),
                );
            }
        } catch {
            // ignore storage parsing errors
        }
    }, [cityIdParam, cityParam, dateValue, pathname, router, searchValue]);
    const handleSearchSubmit = (event: FormEvent) => {
        event.preventDefault();
        setIsSearchOpen(false);
        searchFocusRef.current = false;
        setIsSearchFocused(false);
        setIsDateMenuOpen(false);
        setIsProfileOpen(false);
        router.push(buildEventsUrl(searchValue, cityValue, dateValue, cityIdValue));
    };
    const handleCitySelect = (value: string, id?: string) => {
        hasUserSelectedCityRef.current = true;
        if (typeof window !== "undefined") {
            try {
                window.localStorage.setItem(
                    CITY_STORAGE_KEY,
                    JSON.stringify({ value, id: id ?? "" }),
                );
            } catch {
                // ignore storage write errors
            }
        }
        setCityValue(value);
        setCityIdValue(id ?? "");
        setIsCityMenuOpen(false);
        setIsSearchOpen(false);
        setIsDateMenuOpen(false);
        setIsProfileOpen(false);
        searchFocusRef.current = false;
        setIsSearchFocused(false);
        router.push(buildEventsUrl(searchValue, value, dateValue, id));
    };
    const handleDateChange = (value: string) => {
        setDateValue(value);
        setIsSearchOpen(false);
        setIsDateMenuOpen(false);
        setIsProfileOpen(false);
        searchFocusRef.current = false;
        setIsSearchFocused(false);
        router.push(buildEventsUrl(searchValue, cityValue, value, cityIdValue));
    };
    const openDatePicker = (anchor: "desktop" | "mobile") => {
        setIsDateMenuOpen((prev) => (dateMenuAnchor === anchor ? !prev : true));
        setDateMenuAnchor(anchor);
        setIsCityMenuOpen(false);
        setIsSearchOpen(false);
        setIsProfileOpen(false);
        searchFocusRef.current = false;
        setIsSearchFocused(false);
    };
    const handleSuggestionSelect = (event: SearchEvent) => {
        setIsSearchOpen(false);
        setSearchResults([]);
        searchFocusRef.current = false;
        setIsSearchFocused(false);
        router.push(buildEventPath(event.shortId ?? event.id, event.title));
    };
    const handleSearchFocus = () => {
        searchFocusRef.current = true;
        setIsSearchFocused(true);
        if (hasQuery) {
            setIsSearchOpen(true);
        }
    };

    const selectedDate = parseDateKey(dateValue);
    const initialCursor = selectedDate ?? new Date();
    const [calendarCursor, setCalendarCursor] = useState({
        year: initialCursor.getUTCFullYear(),
        month: initialCursor.getUTCMonth(),
    });

    useEffect(() => {
        if (!dateValue) {
            return;
        }
        const parsed = parseDateKey(dateValue);
        if (!parsed) {
            return;
        }
        setCalendarCursor({
            year: parsed.getUTCFullYear(),
            month: parsed.getUTCMonth(),
        });
    }, [dateValue]);

    useEffect(() => {
        if (!citiesData || citiesData.length === 0) {
            return;
        }
        const resolvedPath =
            typeof window !== "undefined" ? window.location.pathname : pathname;
        // Only auto-apply the default city on home and the events list — NOT on
        // /events/<city>/<slug> detail pages (would bounce the user off the event).
        const allowAutoRoute =
            resolvedPath === "/" || resolvedPath === "/events";
        if (cityValue || cityIdValue) {
            if (cityIdValue && !cityValue) {
                const match = citiesData.find((city) => city.id === cityIdValue);
                if (match) {
                    setCityValue(match.name);
                }
            } else if (!cityIdValue && cityValue) {
                const match = citiesData.find(
                    (city) => city.name.toLowerCase() === cityValue.toLowerCase(),
                );
                if (match) {
                    setCityIdValue(match.id);
                }
            }
            return;
        }
        if (hasUserSelectedCityRef.current) {
            return;
        }
        const defaultCity =
            citiesData.find((city) => city.name.toLowerCase() === "jaipur") ??
            citiesData[0];
        if (!defaultCity) {
            return;
        }
        setCityValue(defaultCity.name);
        setCityIdValue(defaultCity.id);
        if (allowAutoRoute) {
            router.replace(
                buildEventsUrl(searchValue, defaultCity.name, dateValue, defaultCity.id),
            );
        }
    }, [
        citiesData,
        cityIdValue,
        cityValue,
        dateValue,
        pathname,
        router,
        searchValue,
    ]);

    const toggleCityMenu = () => {
        setIsCityMenuOpen((prev) => !prev);
        setIsProfileOpen(false);
        setIsPriceMenuOpen(false);
    };

    const togglePriceMenu = () => {
        setIsPriceMenuOpen((prev) => !prev);
        setIsCityMenuOpen(false);
        setIsProfileOpen(false);
        setIsDateMenuOpen(false);
        setIsSearchOpen(false);
        searchFocusRef.current = false;
        setIsSearchFocused(false);
    };

    const handlePriceApply = () => {
        setIsPriceMenuOpen(false);
        router.push(
            buildEventsUrl(searchValue, cityValue, dateValue, cityIdValue, minPrice, maxPrice),
        );
    };

    const handlePriceClear = () => {
        setMinPrice("");
        setMaxPrice("");
        setIsPriceMenuOpen(false);
        router.push(
            buildEventsUrl(searchValue, cityValue, dateValue, cityIdValue, "", ""),
        );
    };

    const handleProfileToggle = () => setIsProfileOpen((prev) => !prev);

    const handleProfileSignOut = () => {
        setIsProfileOpen(false);
        signOut({ redirectUrl: "/" });
    };

    const handleSignOut = () => {
        signOut({ redirectUrl: "/" });
    };

    const searchPanelProps = {
        searchValue,
        onSearchChange: setSearchValue,
        onSearchSubmit: handleSearchSubmit,
        onSearchFocus: handleSearchFocus,
        hasQuery,
        isSearchFocused,
        isSearchOpen,
        isSearching,
        searchResults,
        onSuggestionSelect: handleSuggestionSelect,
    };

    const dateCalendarProps = {
        isDateMenuOpen,
        dateMenuAnchor,
        dateValue,
        calendarCursor,
        onCalendarCursorChange: setCalendarCursor,
        onDateChange: handleDateChange,
        onOpenDatePicker: openDatePicker,
    };

    const notificationProps = {
        isOpen: isNotificationsOpen,
        onToggle: toggleNotificationsMenu,
        unreadCount,
        notifications,
        isLoading: isLoadingNotifications,
        markAsRead,
        markAllAsRead,
        removeNotification,
    };

    return (
        <>
            <motion.header
                className="sticky left-0 right-0 top-0 z-50 border-b border-white/10 bg-black/95 shadow-[0_16px_40px_rgba(5,8,18,0.45)] backdrop-blur"
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
            >
                <div className="mx-auto w-full max-w-[1400px] px-3 md:px-6 lg:px-10">
                    <motion.div
                        className="relative flex items-center gap-3"
                        initial={false}
                        animate={{
                            paddingTop: isCollapsed ? 6 : 10,
                            paddingBottom: isCollapsed ? 6 : 10,
                        }}
                        transition={{ duration: 0.25, ease: [0.33, 1, 0.68, 1] }}
                    >
                        {/* Mobile notifications - left side */}
                        <div className="lg:hidden">
                            <NotificationsPanel
                                {...notificationProps}
                                containerRef={mobileNotificationsRef}
                                position="left"
                            />
                        </div>

                        <Link
                            href="/"
                            className="group flex items-center justify-center lg:static lg:translate-x-0 absolute left-1/2 -translate-x-1/2"
                        >
                            <Image
                                src="/images/logo.svg"
                                alt="Hopp"
                                width={100}
                                height={28}
                                className="h-7 w-auto transition-opacity group-hover:opacity-80"
                            />
                        </Link>

                        <AnimatePresence initial={false}>
                            {!isCollapsed ? (
                                <motion.div
                                    className="hidden lg:flex flex-1 items-center gap-3"
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.2 }}
                                    style={{ pointerEvents: isCollapsed ? "none" : "auto" }}
                                >
                                    <SearchPanel
                                        {...searchPanelProps}
                                        formRef={desktopSearchRef}
                                        datePickerSlot={
                                            <DateCalendar
                                                {...dateCalendarProps}
                                                anchor="desktop"
                                                containerRef={desktopDateRef}
                                            />
                                        }
                                    />

                                    <CityPicker
                                        cityValue={cityValue}
                                        cityLabel={cityLabel}
                                        cityOptions={cityOptions}
                                        citiesData={citiesData}
                                        isCityMenuOpen={isCityMenuOpen}
                                        onToggle={toggleCityMenu}
                                        onCitySelect={handleCitySelect}
                                        containerRef={desktopCityRef}
                                        showCaret={true}
                                        dropdownAlign="left"
                                    />

                                    <PriceFilter
                                        minPrice={minPrice}
                                        maxPrice={maxPrice}
                                        onMinPriceChange={setMinPrice}
                                        onMaxPriceChange={setMaxPrice}
                                        onApply={handlePriceApply}
                                        onClear={handlePriceClear}
                                        isOpen={isPriceMenuOpen}
                                        onToggle={togglePriceMenu}
                                        containerRef={desktopPriceRef}
                                        showCaret={true}
                                        dropdownAlign="left"
                                    />
                                </motion.div>
                            ) : null}
                        </AnimatePresence>

                        <div className="ml-auto flex items-center gap-2 md:gap-3">
                            <Link
                                href="/map"
                                className={`hidden items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition lg:flex ${
                                    pathname?.startsWith("/map")
                                        ? "border-[var(--hopp-sapphire)] bg-[var(--hopp-panel)] text-[var(--hopp-text)]"
                                        : "border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-[var(--hopp-muted)] hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)]"
                                }`}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" />
                                    <path d="M9 4v14M15 6v14" />
                                </svg>
                                Map
                            </Link>
                            <button className="hidden items-center gap-2 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 py-2 text-sm font-semibold text-[var(--hopp-muted)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)] xl:flex">
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M2 12h20M12 2c3 3 3 15 0 20M12 2c-3 3-3 15 0 20" />
                                </svg>
                                EN / ₹
                            </button>

                            <div className="hidden lg:block">
                                <NotificationsPanel
                                    {...notificationProps}
                                    containerRef={desktopNotificationsRef}
                                    position="right"
                                />
                            </div>

                            <ProfileMenu
                                isSignedIn={isSignedIn}
                                isProfileOpen={isProfileOpen}
                                onToggle={handleProfileToggle}
                                onNavigate={handleProfileNavigation}
                                onSignOut={handleProfileSignOut}
                                displayName={displayName}
                                userImageUrl={user?.imageUrl}
                                userEmail={displayEmailOrNull(user?.primaryEmailAddress?.emailAddress) ?? undefined}
                                profileMenuItems={profileMenuItems}
                                accountHref={accountHref}
                                containerRef={profileRef}
                            />

                            <button
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-[var(--hopp-muted)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)] lg:hidden"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                aria-label="Toggle menu"
                            >
                                <div className="flex flex-col gap-1">
                                    <motion.span
                                        className="block h-0.5 w-5 bg-[var(--hopp-text)]"
                                        animate={{
                                            rotate: isMobileMenuOpen ? 45 : 0,
                                            y: isMobileMenuOpen ? 5 : 0,
                                        }}
                                    />
                                    <motion.span
                                        className="block h-0.5 w-5 bg-[var(--hopp-text)]"
                                        animate={{ opacity: isMobileMenuOpen ? 0 : 1 }}
                                    />
                                    <motion.span
                                        className="block h-0.5 w-5 bg-[var(--hopp-text)]"
                                        animate={{
                                            rotate: isMobileMenuOpen ? -45 : 0,
                                            y: isMobileMenuOpen ? -5 : 0,
                                        }}
                                    />
                                </div>
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        className="flex flex-col gap-2 border-t border-[var(--hopp-panel-strong)] py-2 lg:hidden"
                        initial={false}
                        animate={{
                            maxHeight: isCollapsed ? 0 : 140,
                            opacity: isCollapsed ? 0 : 1,
                            marginTop: isCollapsed ? 0 : 4,
                        }}
                        transition={{ duration: 0.25, ease: [0.33, 1, 0.68, 1] }}
                        style={{
                            overflow:
                                isSearchOpen || isSearching || isDateMenuOpen || isCityMenuOpen || isPriceMenuOpen
                                    ? "visible"
                                    : "hidden",
                            pointerEvents: isCollapsed ? "none" : "auto",
                            borderColor: isCollapsed ? "transparent" : "var(--hopp-panel-strong)",
                        }}
                    >
                        <div className="flex items-center gap-2 lg:hidden">
                            <MobileSearchPanel
                                {...searchPanelProps}
                                formRef={mobileSearchRef}
                                datePickerSlot={
                                    <DateCalendar
                                        {...dateCalendarProps}
                                        anchor="mobile"
                                        containerRef={mobileDateRef}
                                    />
                                }
                            />
                            <CityPicker
                                cityValue={cityValue}
                                cityLabel={cityLabel}
                                cityOptions={cityOptions}
                                citiesData={citiesData}
                                isCityMenuOpen={isCityMenuOpen}
                                onToggle={toggleCityMenu}
                                onCitySelect={handleCitySelect}
                                containerRef={mobileCityRef}
                                showCaret={false}
                                dropdownAlign="right"
                            />
                            <PriceFilter
                                minPrice={minPrice}
                                maxPrice={maxPrice}
                                onMinPriceChange={setMinPrice}
                                onMaxPriceChange={setMaxPrice}
                                onApply={handlePriceApply}
                                onClear={handlePriceClear}
                                isOpen={isPriceMenuOpen}
                                onToggle={togglePriceMenu}
                                containerRef={mobilePriceRef}
                                showCaret={false}
                                dropdownAlign="right"
                            />
                        </div>
                    </motion.div>
                </div>
            </motion.header>

            <MobileNav
                isMobileMenuOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                isSignedIn={isSignedIn}
                isHost={isHost}
                displayName={displayName}
                userImageUrl={user?.imageUrl}
                userEmail={displayEmailOrNull(user?.primaryEmailAddress?.emailAddress) ?? undefined}
                authHref={authHref}
                onNavigate={handleProfileNavigation}
                onSignOut={handleSignOut}
            />

        </>
    );
}

export function LandingNav() {
    return (
        <Suspense fallback={<div className="h-16 w-full" />}>
            <LandingNavInner />
        </Suspense>
    );
}
